import videojs from "./videojs-core.js";

import { createAdSequencer } from "../ads/ad-sequencer.js";
import { buildPlayerDom, showPending } from "./container-dom.js";

import { initPlayer } from "./player-init.js";
import { burstIframes, collapseIframes, safeDestroy } from "./player-lifecycle.js";
import { getDebugMode, createLogger } from "../helpers/logger.js";
import { dispatchCleonNoAd } from "../helpers/cleon-events.js";

export async function switchToOMP(container, settings, state, basePlayerId) {
  const debug = getDebugMode(container);
  const log = createLogger("OmpMode", debug);

  if (container.__cleonSwitchingToOMP) {
    log.debug("OMP switch already in progress");
    return;
  }

  container.__cleonSwitchingToOMP = true;


  try {
    log.debug("🔁 Switching to clean OMP player");
  
    const freshState = {
      ...state,
      prerollIndex: 0,
      fallbackIndex: 0,
      midrollIndex: 0,
      postrollIndex: 0,
      currentType: null,
      fallbackUrls: state.fallbackUrls || [],
      midrollUrls: state.midrollUrls || [],
      postrollUrls: state.postrollUrls || [],
    };
  
    const existingVideo = container.querySelector("video");
    const existingId = existingVideo?.id || basePlayerId;
  
    try {
      const oldPlayer = videojs.getPlayer(existingId);
      if (oldPlayer && !oldPlayer.isDisposed?.()) {
        oldPlayer.__destroying = true;
        oldPlayer.dispose();
      }
    } catch (e) {
      log.debug("Failed to dispose old player", e);
    }
  
    buildPlayerDom(container, basePlayerId);
  
    const wrapperSelector = `#${container.id} .video-wrapper-fluid, #${container.id} .video-wrapper-fixed`;
    const playerContainerSelector = `#${container.id} .player-container`;
  
    const { player, dom } = await initPlayer(settings, {
      videoElementId: basePlayerId,
      playerContainerSelector,
      wrapperSelector,
      imaOptions: {
        id: basePlayerId,
      },
    });
  
    if (!player) {
      log.debug("OMP initPlayer returned no player");
      return;
    }
  
    try {
      initOMPStandard(container, settings, freshState, player, dom);
    } catch (e) {
      log.debug("OMP init failed", e);
      throw e;
    }
  } finally {
    container.__cleonSwitchingToOMP = false;  
  }
}

function initOMPStandard(container, settings, state, player, dom) {
  const debug = getDebugMode(container);
  const log = createLogger("OmpMode", debug);
  log.debug("🎬 STANDARD MODE → Starting OMP fallback");

  const fallback = state.fallbackUrls || [];
  const adsOptional = state.adsOptional === true;

  const playlist = settings.playlist || [];
  state.playIndex = settings.playIndex ?? 0;
  state.__postrollPending = false;

  function loadVideoAt(i) {
    if (!playlist.length) return;

    state.playIndex = (i + playlist.length) % playlist.length;
    const item = playlist[state.playIndex];

    player.midrollPlayed = false;
    player.postrollPlayed = false;

    try {
      player.pause();
    } catch {}

    player.poster(item.poster || "");
    player.src({
      src: item.src,
      type:
        item.type ||
        (item.src?.endsWith(".m3u8")
          ? "application/x-mpegURL"
          : "video/mp4"),
    });

    player.one("loadedmetadata", () => {
      try {
        player.currentTime(0);
      } catch {}
    });
  }

  function goNextVideo() {
    loadVideoAt(state.playIndex + 1);
  }

  // container.hidden = false;

  state.fallbackIndex = 0;
  state.ompAdsDone = false;
  state.__restartAfterPostroll = false;

  player.__adPlaying = false;
  player.__lockContent = !adsOptional;

  let started = false;
  let revealTimer = null;
  let adVisible = false;
  let adFailed = false;
  let destroyedByFallbackFailure = false;

  function revealPlayerForContent(reason = "content") {
    if (destroyedByFallbackFailure) return;
    if (player.__destroying || player.isDisposed?.()) return;

    adVisible = true;
    state.ompAdsDone = true;
    player.__lockContent = false;
    player.__adPlaying = false;

    try {
      container.hidden = false;
      container.removeAttribute("hidden");
      container.style.display = "block";
      container.style.visibility = "visible";
      container.style.opacity = "1";
    } catch {}

    showPending(container);
    burstIframes();
    player.controls(true);

    if (settings?.autoplay) {
      try {
        player.muted(true);
      } catch {}

      player.play().catch((e) => log.debug("content autoplay failed", e));
    }

    log.debug("▶ monetized content revealed", { reason });
  }

  function clearRevealTimer() {
    if (revealTimer) {
      clearTimeout(revealTimer);
      revealTimer = null;
    }
  }

  function cleanupAdListeners() {
    try { player.off("imaerror", handleAdUiError); } catch {}
    try { player.off("ima3error", handleAdUiError); } catch {}
    try { player.off("adserror", handleAdUiError); } catch {}
    try { player.off("ads-ad-ended", handleAdEnded); } catch {}
    try { player.off("adend", handleAdEnded); } catch {}
    try { player.off("ads-ad-started", handleAdStarted); } catch {}
    try { player.off("play", playLock); } catch {}
  }

  function destroyAfterFallbackFailure(reason = "fallback-failed") {
    if (destroyedByFallbackFailure) return;
    destroyedByFallbackFailure = true;

    adFailed = true;
    clearRevealTimer();

    state.ompAdsDone = true;
    player.__adPlaying = false;
    player.__lockContent = false;

    cleanupAdListeners();

    try {
      player.pause();
    } catch {}

    try {
      container.classList?.remove("ad-slot--pending");
      container.hidden = false;
      container.removeAttribute?.("hidden");
      container.style.setProperty("display", "block", "important");
      container.style.setProperty("visibility", "visible", "important");
      container.style.setProperty("opacity", "1", "important");
    } catch {}

    try {
      player.pause();
    } catch {}
      
    safeDestroy(player, dom, settings, container, "yes", debug);
  }

  const playLock = (e) => {
    if (adsOptional) return;
    if (destroyedByFallbackFailure) return false;
    if (state.ompAdsDone) return;
    if (player.__adPlaying) return;
    if (!player.__lockContent) return;

    e?.preventDefault?.();
    e?.stopPropagation?.();

    try {
      player.pause();
    } catch {}

    return false;
  };

  function revealPlayerForAd() {
    if (destroyedByFallbackFailure) return;
    if (adVisible) return;
    if (player.__destroying || player.isDisposed?.()) return;

    adVisible = true;

    try {
      container.hidden = false;
      container.removeAttribute("hidden");
      container.style.display = "block";
      container.style.visibility = "visible";
      container.style.opacity = "1";
    } catch {}

    showPending(container);
    burstIframes();

    log.debug("👁 ad stable → reveal player");
  }

  function scheduleRevealAfterStableStart() {
    clearRevealTimer();
    adFailed = false;

    revealTimer = setTimeout(() => {
      revealTimer = null;

      if (destroyedByFallbackFailure) {
        log.debug("❌ not revealing because fallback already destroyed player");
        return;
      }

      if (adFailed) {
        log.debug("❌ not revealing because ad already failed");
        return;
      }

      if (player.__destroying || player.isDisposed?.()) {
        log.debug("❌ not revealing because player is gone");
        return;
      }

      if (!player.__adPlaying) {
        log.debug("❌ not revealing because ad is no longer playing");
        return;
      }

      revealPlayerForAd();
    }, 1000);
  }

  const startAdBreak = createAdSequencer(player, state, {
    container,
    settings,
    playLock,
    debug,
    onFallbackBreakFailed: ({ type, reason, waterfallExhausted, hadAdStarted, shouldDestroy }) => {
      log.debug("⛔ Fallback waterfall exhausted with no ad", {
        type,
        reason,
        waterfallExhausted,
        hadAdStarted,
        shouldDestroy,
      });

      dispatchCleonNoAd(container, settings, "fallback_failed", {
        fallbackReason: reason || "no-more-urls",
        waterfallExhausted: true,
        hadAdStarted,
      });

      if (adsOptional) {
        revealPlayerForContent(reason || "fallback-failed");
        return;
      }

      if (shouldDestroy) {
        destroyAfterFallbackFailure(reason || "fallback-failed");
      }
    },
  });

  const startOnce = () => {
    

    if (destroyedByFallbackFailure) return;
    if (started) return;
    started = true;
    log.debug("▶ startOnce()", {
      started,
      lockContent: player.__lockContent,
      ompAdsDone: state.ompAdsDone,
    });

    

    if (player.ima?.initializeAdDisplayContainer) {
      log.debug("🧩 initializeAdDisplayContainer()");
      player.ima.initializeAdDisplayContainer();
    }

    if (fallback.length === 0) {
      dispatchCleonNoAd(container, settings, "no_omp_fallback_ads", {
        fallbackCount: fallback.length,
      });

      if (adsOptional) {
        revealPlayerForContent("no-fallback-ads");
        return;
      }

      safeDestroy(player, dom, settings, container, "yes", debug);
      return;
    }

    state.ompAdsDone = adsOptional;
    player.__lockContent = !adsOptional;
    player.__adPlaying = false;

    log.debug("🚀 startAdBreak('fallback')", { adsOptional });
    startAdBreak("fallback");
  };

  function handleAdStarted() {
    if (destroyedByFallbackFailure) return;
    player.__adPlaying = true;
    scheduleRevealAfterStableStart();
  }

  function handleAdUiError() {
    if (destroyedByFallbackFailure) return;

    adFailed = true;
    clearRevealTimer();
    player.__adPlaying = false;
  }

  function handleAdEnded() {
    clearRevealTimer();
    player.__adPlaying = false;
  }

  player.ready(() => {
    if (destroyedByFallbackFailure) return;

    loadVideoAt(state.playIndex);

    if (!settings?.autoplay) {
      try {
        player.pause();
      } catch {}
    }

    player.on("ads-ad-started", handleAdStarted);
    player.on("imaerror", handleAdUiError);
    player.on("ima3error", handleAdUiError);
    player.on("adserror", handleAdUiError);
    player.on("ads-ad-ended", handleAdEnded);
    player.on("adend", handleAdEnded);

    if (adsOptional) {
      revealPlayerForContent("monetized-ads-optional");
    } else {
      player.on("play", playLock);
      player.controls(false);
    }

    startOnce();

    if (window.PointerEvent) {
      container.addEventListener("pointerdown", startOnce, {
        once: true,
        passive: true,
      });
    } else {
      container.addEventListener("touchstart", startOnce, {
        once: true,
        passive: true,
      });
      container.addEventListener("mousedown", startOnce, {
        once: true,
        passive: true,
      });
    }

    if (settings?.autoplay) {
      player.muted(true);
    }
  });
  const hasMidroll = (state.midrollUrls?.length || 0) > 0;
  const hasPostroll = (state.postrollUrls?.length || 0) > 0;
  
  player.on("timeupdate", () => {
    if (destroyedByFallbackFailure) return;
    if (!state.ompAdsDone || player.__adPlaying) return;
    if (player.isDisposed?.() || player.__destroying) return;
    if (player.readyState?.() < 1) return;

    const dur = player.duration();
    if (!dur || !Number.isFinite(dur) || dur <= 0) return;

    const ct = player.currentTime();
    if (!Number.isFinite(ct)) return;

    if (hasMidroll && ct >= 60 && !player.midrollPlayed) {
      player.midrollPlayed = true;
      startAdBreak("midroll");
    }

    if (hasPostroll && ct >= dur - 3 && !player.postrollPlayed) {
      player.postrollPlayed = true;
      startAdBreak("postroll");
    }
  });

  player.on("ended", () => {
    if (destroyedByFallbackFailure) return;

    state.ompAdsDone = false;
    state.fallbackIndex = 0;
    state.midrollIndex = 0;
    state.postrollIndex = 0;
    goNextVideo();
  });
}